import {
  Block,
  Spec,
  Chips,
  Prose,
  str,
  thousands,
  range,
  fmtDateTime,
  safeHref,
} from "./SubmissionBlocks";
import { humanise } from "@shared/websiteSubmissions";

/**
 * Buyers list submission detail.
 *
 * The website studio has no equivalent view — buyer_profiles was never given
 * one — so this is the first place a buyer registration can be read in full.
 */
export function BuyerDetail({ payload: p }: { payload: Record<string, unknown> }) {
  // Scheme-checked: this is a free-text field on a public form, and an
  // unchecked `javascript:` here executes in the CRM's origin on click.
  const onePager = safeHref(p.onePagerUrl);
  const onePagerRaw = str(p.onePagerUrl);

  return (
    <div className="space-y-4">
      <Block title="Contact">
        <Spec label="Name" value={str(p.name)} />
        <Spec label="Title" value={str(p.title)} />
        <Spec
          label="Email"
          value={
            str(p.email) ? (
              <a className="text-primary-text hover:underline" href={`mailto:${p.email}`}>
                {String(p.email)}
              </a>
            ) : null
          }
        />
        <Spec
          label="Phone"
          value={
            str(p.phone) ? (
              <a className="text-primary-text hover:underline" href={`tel:${p.phone}`}>
                {String(p.phone)}
              </a>
            ) : null
          }
        />
      </Block>

      <Block title="Firm">
        <Spec label="Firm name" value={str(p.firmName)} />
        <Spec label="Firm type" value={humanise(str(p.firmType))} />
        <div className="pt-2">
          <div className="mb-1 text-xs text-muted-foreground">Investment thesis</div>
          <Prose text={p.investmentThesis} />
        </div>
      </Block>

      <Block title="Mandate">
        <Spec label="Industry breadth" value={humanise(str(p.industryBreadth))} />
        <Spec label="Geography breadth" value={humanise(str(p.geographyBreadth))} />
        <Spec label="Industry categories" value={<Chips values={p.industryCategories} />} />
        <Spec label="Industry focus" value={<Chips values={p.industryFocus} />} />
        <Spec label="Industry (other)" value={str(p.industryOther)} />
        <Spec label="Investment themes" value={<Chips values={p.investmentThemes} />} />
        <Spec label="Geographic focus" value={<Chips values={p.geographicFocus} />} />
      </Block>

      {/* These four columns are stored IN THOUSANDS upstream (5000 = $5M).
          Formatting them as raw dollars understates every buyer by 1000x. */}
      <Block title="Deal parameters">
        <Spec label="Deal size" value={range(p.dealSizeMin, p.dealSizeMax, thousands)} />
        <Spec label="Minimum EBITDA" value={thousands(p.ebitdaMin)} />
        <Spec label="Minimum revenue" value={thousands(p.revenueMin)} />
      </Block>

      <Block title="Other">
        <Spec
          label="One-pager"
          value={
            onePager ? (
              <a
                className="text-primary-text hover:underline"
                href={onePager}
                target="_blank"
                rel="noopener noreferrer"
              >
                View PDF
              </a>
            ) : onePagerRaw ? (
              // Something was submitted but it is not a usable http(s) URL.
              // Show it as text so the record stays honest, never as a link.
              <span className="break-all font-mono text-xs text-muted-foreground">
                {onePagerRaw} <span className="italic">(not a usable link)</span>
              </span>
            ) : null
          }
        />
        <Spec label="Notes" value={str(p.notes)} />
        <Spec label="Active" value={p.isActive === false ? "No" : "Yes"} />
        <Spec label="Registered" value={fmtDateTime(p.createdAt)} />
        <Spec label="Last updated" value={fmtDateTime(p.updatedAt)} />
      </Block>
    </div>
  );
}
